import * as PIXI from 'pixi.js';
import Audio from '~/Audio';
import { ShopStore } from '~/flux/stores';
import text, { getShopkeeperMessage } from '~/text';
import CanvasTools from '~/view/CanvasTools';
import ShopInventoryShelves from './ShopInventoryShelves';

const Shop = function ({ show_tips_bubble, onItemPurchaseConfirmed } = {}) {
  PIXI.Container.call(this);

  let _backdrop;
  let _shopKeeper;
  let _shopInventoryShelves;
  let _transitioningShelves;
  let _lastRestockTimestamp = ShopStore.getAll().lastRestock;

  this.transitionIn = () => {
    if (_shopInventoryShelves) {
      TweenMax.from(_shopInventoryShelves, 0.25, { alpha: 0 });
    }

    if (_shopKeeper) {
      TweenMax.from(_shopKeeper, 0.25, { alpha: 0 }).delay(0.3);
    }
  };

  this.transitionOut = (onComplete) => {
    if (_shopInventoryShelves) {
      TweenMax.to(_shopInventoryShelves, 0.25, { alpha: 0 });
    }

    if (_shopKeeper) {
      TweenMax.to(_shopKeeper, 0.25, { alpha: 0, onComplete });
    }
  };

  this.dispose = () => {
    ShopStore.removeListener(ShopStore.SHOP_INVENTORY, onShopInventory);

    destroyBackdrop();
    destroyShopKeeper();
    destroyShopInventoryShelves();

    DT_CANVAS_GLOBALS.poolSafeDestroy(textBubble, { children: true });
    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };

  this.handleResize = () => {
    makeBackdrop();
    makeShopKeeper();
    makeShopInventoryShelves();
  };

  const destroyBackdrop = () => {
    DT_CANVAS_GLOBALS.poolSafeDestroy(_backdrop, { children: true });
  };

  const destroyShopInventoryShelves = () => {
    if (_shopInventoryShelves) {
      TweenMax.killTweensOf(_shopInventoryShelves);
      _shopInventoryShelves.dispose();
      this.removeChild(_shopInventoryShelves);
      _shopInventoryShelves = null;
    }
    if (_transitioningShelves) {
      TweenMax.killTweensOf(_transitioningShelves);
      _transitioningShelves.dispose();
      this.removeChild(_transitioningShelves);
      _transitioningShelves = null;
    }
  };

  const destroyShopKeeper = () => {
    DT_CANVAS_GLOBALS.poolSafeDestroy(_shopKeeper, { children: true });
  };

  const makeBackdrop = () => {
    destroyBackdrop();

    _backdrop = new PIXI.Container();
    _backdrop._width = window.innerWidth;
    _backdrop._height = window.innerHeight;
    _backdrop = CanvasTools.addBackFill(_backdrop, 0x000000);
    _backdrop.alpha = 0.01;
    this.addChild(_backdrop);

    _backdrop.interactive = true;
    _backdrop.mousedown = _backdrop.touchstart = () => {
      _shopInventoryShelves.hidePopups();
    };
  };

  const makeShopInventoryShelves = () => {
    destroyShopInventoryShelves();

    _shopInventoryShelves = this.shopInventoryShelves = new ShopInventoryShelves({ onItemPurchaseConfirmed });
    _shopInventoryShelves.x = Math.round(
      window.innerWidth * 0.71 - _shopInventoryShelves.width / 2
    );
    _shopInventoryShelves.y = Math.round(
      50 + window.innerHeight * 0.18 - _shopInventoryShelves.height / 2
    );
    this.addChild(_shopInventoryShelves);
  };

  const makeShopKeeper = () => {
    destroyShopKeeper();

    _shopKeeper = new PIXI.Sprite();
    _shopKeeper.texture = PIXI.utils.TextureCache['shop/shopkeeper_portrait.png'];
    _shopKeeper.height = Math.min(_shopKeeper.height, window.innerHeight * 0.3);
    _shopKeeper.scale.x = _shopKeeper.scale.y;
    _shopKeeper.x = window.innerWidth - _shopKeeper.width - DT_CANVAS_GLOBALS.spacing * 3;
    _shopKeeper.y = window.innerHeight * 0.74 - _shopKeeper.height - DT_CANVAS_GLOBALS.spacing * 3;
    this.addChild(_shopKeeper);

    this.onItemsToSellChange({ show_tips_bubble });
  };

  let textBubble;
  let speechText;
  this.onItemsToSellChange = ({ show_tips_bubble }) => {
    DT_CANVAS_GLOBALS.poolSafeDestroy(textBubble, { children: true });

    if (show_tips_bubble) {
      textBubble = new PIXI.Sprite();
      textBubble.texture = PIXI.utils.TextureCache['shop/shopkeeper_text_bubble.png'];
      textBubble.x = -textBubble.width * 1.2;
      textBubble.y = textBubble.height * 0.6;
      _shopKeeper.addChild(textBubble);

      speechText = _shopKeeper.speechText = new PIXI.Text(
        ShopStore.getAll().shopInventory.length
          ? getShopkeeperMessage()
          : text('ui.shop.out_of_stock_msg'),
        {
          fontFamily: 'Courier New',
          fontSize: '16px',
          fill: 0x000000,
          wordWrap: true,
          wordWrapWidth: textBubble.width * 0.85,
        }
      );
      speechText.x = textBubble.width * 0.46 - speechText.width / 2;
      speechText.y = textBubble.height / 2 - speechText.height / 2;
      textBubble.addChild(speechText);
    }
  };

  const onShopInventory = () => {
    const currentRestockTimestamp = ShopStore.getAll().lastRestock;
    const isRestock = currentRestockTimestamp !== _lastRestockTimestamp;
    _lastRestockTimestamp = currentRestockTimestamp;

    // Only animate if this is an actual restock (timestamp changed)
    if (_shopInventoryShelves && isRestock) {
      _transitioningShelves = _shopInventoryShelves;
      const targetX = _transitioningShelves.x;
      const targetY = _transitioningShelves.y;

      // Tween old shelves out to the right
      TweenMax.to(_transitioningShelves, 0.4, {
        x: window.innerWidth + _transitioningShelves.width,
        ease: 'power2.in',
        onComplete: () => {
          // Destroy old shelves
          if (_transitioningShelves) {
            _transitioningShelves.dispose();
            this.removeChild(_transitioningShelves);
            _transitioningShelves = null;
          }

          // Create new shelves
          _shopInventoryShelves = this.shopInventoryShelves = new ShopInventoryShelves({ onItemPurchaseConfirmed });
          _shopInventoryShelves.x = window.innerWidth + _shopInventoryShelves.width;
          _shopInventoryShelves.y = targetY;
          this.addChild(_shopInventoryShelves);

          // Tween new shelves in from the right
          TweenMax.to(_shopInventoryShelves, 0.4, {
            x: targetX,
            ease: 'power2.out',
          });

          Audio.play('equipment_storage');
        },
      });

      // Clear reference so new shelves don't get destroyed during transition
      _shopInventoryShelves = null;
    } else {
      // First load or purchase (not a restock), no animation
      makeShopInventoryShelves();
      Audio.play('equipment_storage');
    }
  };

  // init
  makeBackdrop();
  makeShopKeeper();
  makeShopInventoryShelves();
  ShopStore.on(ShopStore.SHOP_INVENTORY, onShopInventory);
};
Shop.prototype = Object.create(PIXI.Container.prototype);
Shop.prototype.constructor = Shop;
export default Shop;
